import type {
  LessonMetadataTranslationPatch,
  LessonMetadataTranslationProjection,
  LessonThemeColor,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import type { AutosaveStatus } from '../../hooks/useAutosave';
import { useScopedMetadataAutosave } from '../../hooks/useScopedMetadataAutosave';

export interface LessonMetadataDraft {
  title: string;
  description: string;
  estimatedTimeMinutes?: number;
  themeColor?: LessonThemeColor;
}

export function metadataDraftFromProjection(
  projection: LessonMetadataTranslationProjection,
): LessonMetadataDraft {
  return {
    title: projection.title,
    description: projection.description,
    estimatedTimeMinutes: projection.estimatedTimeMinutes ?? undefined,
    themeColor: projection.themeColor ?? undefined,
  };
}

export function buildLessonMetadataPatch(
  baseline: LessonMetadataDraft,
  current: LessonMetadataDraft,
  includeEstimatedTime: boolean,
): LessonMetadataTranslationPatch {
  const patch: LessonMetadataTranslationPatch = {};
  if (current.title !== baseline.title) patch.title = current.title;
  if (current.description !== baseline.description) patch.description = current.description;
  if (
    includeEstimatedTime
    && (current.estimatedTimeMinutes ?? null) !== (baseline.estimatedTimeMinutes ?? null)
  ) {
    patch.estimatedTimeMinutes = current.estimatedTimeMinutes ?? null;
  }
  if (includeEstimatedTime && (current.themeColor ?? null) !== (baseline.themeColor ?? null)) {
    patch.themeColor = current.themeColor ?? null;
  }
  return patch;
}

export interface UseLessonMetadataAutosaveOptions {
  enabled: boolean;
  scopeKey: string;
  initialProjection: LessonMetadataTranslationProjection | undefined;
  includeEstimatedTime: boolean;
  replacementAction?: TranslationReplacementAction;
  save: (patch: LessonMetadataTranslationPatch) => Promise<LessonMetadataTranslationProjection>;
  onSaved?: (
    projection: LessonMetadataTranslationProjection,
    patch: LessonMetadataTranslationPatch,
  ) => void;
  intervalMs?: number;
}

export interface UseLessonMetadataAutosaveResult {
  draft: LessonMetadataDraft;
  setDraft: React.Dispatch<React.SetStateAction<LessonMetadataDraft>>;
  status: AutosaveStatus;
  flushIfDirty: () => Promise<void>;
  saveDecision: (action: TranslationReplacementAction) => Promise<void>;
  hasPendingSourceChanges: () => boolean;
  stageReplacementAction: (action: TranslationReplacementAction | undefined) => void;
}

export function useLessonMetadataAutosave({
  enabled,
  scopeKey,
  initialProjection,
  includeEstimatedTime,
  replacementAction,
  save,
  onSaved,
  intervalMs,
}: UseLessonMetadataAutosaveOptions): UseLessonMetadataAutosaveResult {
  return useScopedMetadataAutosave({
    enabled,
    scopeKey,
    initialProjection,
    projectionRevisionKey: [
      initialProjection?.title,
      initialProjection?.description,
      initialProjection?.estimatedTimeMinutes,
      initialProjection?.themeColor,
    ].join('\u0000'),
    replacementAction,
    save,
    onSaved,
    intervalMs,
    emptyDraft: { title: '', description: '' },
    draftFromProjection: metadataDraftFromProjection,
    buildPatch: (baseline, current) => (
      buildLessonMetadataPatch(baseline, current, includeEstimatedTime)
    ),
    hasSourceChanges: (patch) => 'title' in patch || 'description' in patch,
    mergeNewerDraft,
    buildDecisionPatch: (baseline, current, action) => ({
      ...buildLessonMetadataPatch(baseline, current, includeEstimatedTime),
      replacementAction: action,
    }),
  });
}

function mergeNewerDraft(
  serverDraft: LessonMetadataDraft,
  submittedDraft: LessonMetadataDraft,
  currentDraft: LessonMetadataDraft,
): LessonMetadataDraft {
  return {
    title: currentDraft.title !== submittedDraft.title ? currentDraft.title : serverDraft.title,
    description: currentDraft.description !== submittedDraft.description
      ? currentDraft.description
      : serverDraft.description,
    estimatedTimeMinutes: currentDraft.estimatedTimeMinutes !== submittedDraft.estimatedTimeMinutes
      ? currentDraft.estimatedTimeMinutes
      : serverDraft.estimatedTimeMinutes,
    themeColor: currentDraft.themeColor !== submittedDraft.themeColor
      ? currentDraft.themeColor
      : serverDraft.themeColor,
  };
}
